import React, { useState, useEffect } from 'react';
import { postsAPI, authAPI } from '../utils/api';

const PostDetailModal = ({ post, onClose }) => {
  const [postDetails, setPostDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (post) {
      loadPostDetails();
      loadUser();
    }
  }, [post]);

  const loadUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (err) {
      // User not logged in
      setUser(null);
    }
  };

  const loadPostDetails = async () => {
    try {
      setLoading(true);
      
      // If we have a camping spot ID, get posts for that spot
      if (post.campingSpotId) {
        const response = await postsAPI.getPostsByCampingSpot(post.campingSpotId);
        const topPost = response.data.find(p => p.isTopPost);
        setPostDetails(topPost || response.data[0] || null);
      } else if (post.id) {
        // Load specific post
        const response = await postsAPI.getPostById(post.id);
        setPostDetails(response.data);
      }
    } catch (err) {
      console.error('Error loading post details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNextMedia = () => {
    if (!postDetails || postDetails.media.length === 0) return;
    setCurrentMediaIndex((prev) => (prev + 1) % postDetails.media.length);
  };

  const handlePrevMedia = () => {
    if (!postDetails || postDetails.media.length === 0) return;
    setCurrentMediaIndex((prev) => 
      prev === 0 ? postDetails.media.length - 1 : prev - 1
    );
  };

  const handleCommentSubmit = async (mediaId) => {
    if (!newComment.trim()) return;

    try {
      const response = await postsAPI.createComment({
        postMediaId: mediaId,
        content: newComment,
      });

      // Add new comment to local state
      const currentMedia = postDetails.media.find(m => m.id === mediaId);
      if (currentMedia) {
        setComments(prev => ({
          ...prev,
          [mediaId]: [...(prev[mediaId] || currentMedia.comments || []), response.data]
        }));
      }

      setNewComment('');
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('Failed to post comment. Please make sure you are logged in.');
    }
  };

  if (loading) {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!postDetails) {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No posts available for this location.</p>
            <button onClick={onClose} style={buttonStyle}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const currentMedia = postDetails.media[currentMediaIndex];
  const hasMedia = postDetails.media.length > 0;

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            {postDetails.userAvatar && (
              <img 
                src={postDetails.userAvatar} 
                alt={postDetails.userName}
                style={avatarStyle}
              />
            )}
            <span style={{ marginLeft: '10px', fontWeight: '600' }}>
              {postDetails.userName}
            </span>
            {postDetails.isTopPost && (
              <span style={{ 
                marginLeft: '10px', 
                padding: '2px 8px', 
                backgroundColor: '#FFD700',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                ⭐ Top Post
              </span>
            )}
          </div>
          <button onClick={onClose} style={closeButtonStyle}>×</button>
        </div>

        {/* Media Slider */}
        {hasMedia && (
          <div style={mediaContainerStyle}>
            {currentMedia.mediaType === 'video' ? (
              <video
                src={currentMedia.url}
                controls
                style={mediaStyle}
              />
            ) : (
              <img
                src={currentMedia.url}
                alt={`Post media ${currentMediaIndex + 1}`}
                style={mediaStyle}
              />
            )}

            {/* Navigation buttons */}
            {postDetails.media.length > 1 && (
              <>
                <button 
                  onClick={handlePrevMedia}
                  style={navButtonStyle('left')}
                >
                  ‹
                </button>
                <button 
                  onClick={handleNextMedia}
                  style={navButtonStyle('right')}
                >
                  ›
                </button>

                {/* Indicators */}
                <div style={indicatorsStyle}>
                  {postDetails.media.map((_, index) => (
                    <div
                      key={index}
                      style={{
                        ...indicatorStyle,
                        backgroundColor: index === currentMediaIndex ? '#4285F4' : 'rgba(255,255,255,0.5)'
                      }}
                    />
                  ))}
                </div>

                {/* Counter */}
                <div style={counterStyle}>
                  {currentMediaIndex + 1} / {postDetails.media.length}
                </div>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div style={contentStyle}>
          <p style={{ margin: 0, lineHeight: '1.5' }}>
            {postDetails.content}
          </p>
          <span style={{ fontSize: '12px', color: '#999' }}>
            {new Date(postDetails.createdAt).toLocaleDateString()}
          </span>
        </div>

        {/* Comment Section */}
        <div style={commentSectionStyle}>
          {/* Comments for current media */}
          {currentMedia && (
            <div style={{ marginTop: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                Comments ({currentMedia.comments?.length || 0})
              </h4>
              
              {/* Comment list */}
              <div style={commentListStyle}>
                {(comments[currentMedia.id] || currentMedia.comments || []).map((comment) => (
                  <div key={comment.id} style={commentItemStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                      {comment.userAvatar && (
                        <img 
                          src={comment.userAvatar} 
                          alt={comment.userName}
                          style={smallAvatarStyle}
                        />
                      )}
                      <span style={{ 
                        marginLeft: '8px', 
                        fontWeight: '600',
                        fontSize: '13px'
                      }}>
                        {comment.userName}
                      </span>
                    </div>
                    <p style={{ margin: '0', fontSize: '13px' }}>
                      {comment.content}
                    </p>
                    <span style={{ fontSize: '11px', color: '#999' }}>
                      {new Date(comment.createdAt).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Comment input */}
              {user ? (
                <div style={commentInputContainerStyle}>
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCommentSubmit(currentMedia.id);
                      }
                    }}
                    style={commentInputStyle}
                  />
                  <button
                    onClick={() => handleCommentSubmit(currentMedia.id)}
                    disabled={!newComment.trim()}
                    style={{
                      ...submitButtonStyle,
                      opacity: newComment.trim() ? 1 : 0.5
                    }}
                  >
                    Post
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <button style={loginButtonStyle}>Log in to comment</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  maxWidth: '600px',
  maxHeight: '90vh',
  width: '95%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px 20px',
  borderBottom: '1px solid #eee',
};

const avatarStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
};

const smallAvatarStyle = {
  width: '30px',
  height: '30px',
  borderRadius: '50%',
  objectFit: 'cover',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '28px',
  cursor: 'pointer',
  color: '#666',
  padding: '0',
  lineHeight: 1,
};

const mediaContainerStyle = {
  position: 'relative',
  backgroundColor: '#000',
};

const mediaStyle = {
  width: '100%',
  height: 'auto',
  maxHeight: '400px',
  objectFit: 'contain',
  display: 'block',
};

const navButtonStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: '10px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  fontSize: '24px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.2s',
});

const indicatorsStyle = {
  position: 'absolute',
  bottom: '50px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '6px',
};

const indicatorStyle = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  transition: 'background 0.2s',
};

const counterStyle = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: '4px 10px',
  borderRadius: '12px',
  fontSize: '12px',
};

const contentStyle = {
  padding: '15px 20px',
  borderBottom: '1px solid #eee',
};

const commentSectionStyle = {
  padding: '15px 20px',
  flex: 1,
  overflowY: 'auto',
};

const commentListStyle = {
  maxHeight: '200px',
  overflowY: 'auto',
  marginBottom: '15px',
};

const commentItemStyle = {
  padding: '10px',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
  marginBottom: '8px',
};

const commentInputContainerStyle = {
  display: 'flex',
  gap: '10px',
};

const commentInputStyle = {
  flex: 1,
  padding: '10px 15px',
  borderRadius: '20px',
  border: '1px solid #ddd',
  fontSize: '14px',
};

const submitButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#4285F4',
  color: 'white',
  border: 'none',
  borderRadius: '20px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
};

const loginButtonStyle = {
  padding: '8px 16px',
  backgroundColor: '#4285F4',
  color: 'white',
  border: 'none',
  borderRadius: '20px',
  cursor: 'pointer',
  fontSize: '14px',
};

const buttonStyle = {
  padding: '10px 20px',
  backgroundColor: '#4285F4',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

export default PostDetailModal;
